import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import './Documents.css';

const DocumentManagement = () => {
  const { documents, setDocuments, cases, addAuditLog, user } = useApp();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    caseId: '',
    documentType: 'Petition',
    documentName: '',
    description: '',
    uploadDate: new Date().toISOString().split('T')[0],
    accessLevel: 'Public',
  });
  const [editingId, setEditingId] = useState(null);

  const documentTypes = ['Petition', 'Evidence', 'Order', 'Judgment', 'Affidavit', 'Other'];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const caseItem = cases.find(c => c.id === formData.caseId);
    
    if (editingId) {
      setDocuments(documents.map(doc => 
        doc.id === editingId 
          ? { 
              ...doc, 
              ...formData,
              caseNumber: caseItem?.caseNumber || doc.caseNumber,
            }
          : doc
      ));
      addAuditLog('Document Updated', `Document ${formData.documentName} updated`);
    } else {
      const newDocument = {
        id: Date.now().toString(),
        ...formData,
        caseNumber: caseItem?.caseNumber || '',
        uploadedBy: user?.username || 'System',
        fileUrl: `#document-${Date.now()}`, // Mock file URL
        createdAt: new Date().toISOString(),
      };
      setDocuments([...documents, newDocument]);
      addAuditLog('Document Uploaded', `Document ${formData.documentName} uploaded for case ${caseItem?.caseNumber}`);
    }
    
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      caseId: '',
      documentType: 'Petition',
      documentName: '',
      description: '',
      uploadDate: new Date().toISOString().split('T')[0],
      accessLevel: 'Public',
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (doc) => {
    setFormData(doc);
    setEditingId(doc.id);
    setShowForm(true);
  };

  const handleDelete = (id) => {
    const doc = documents.find(d => d.id === id);
    setDocuments(documents.filter(d => d.id !== id));
    addAuditLog('Document Deleted', `Document ${doc.documentName} deleted`);
  };

  const canAccess = (doc) => {
    if (doc.accessLevel === 'Public') return true;
    if (user?.role === 'Admin' || user?.role === 'Clerk') return true;
    if (doc.accessLevel === 'Judge' && user?.role === 'Judge') return true;
    if (doc.accessLevel === 'Advocate' && (user?.role === 'Advocate' || user?.role === 'Judge')) return true;
    return false;
  };

  return (
    <div className="documents-container">
      <div className="documents-header">
        <h1>📄 Document Management</h1>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary">
          {showForm ? 'Cancel' : '+ Upload Document'}
        </button>
      </div>

      {showForm && (
        <div className="documents-form-card">
          <h2>{editingId ? 'Edit Document' : 'Upload New Document'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label>Case *</label>
                <select
                  value={formData.caseId}
                  onChange={(e) => setFormData({...formData, caseId: e.target.value})}
                  required
                >
                  <option value="">Select Case</option>
                  {cases.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.caseNumber} - {c.caseTitle}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Document Type *</label>
                <select
                  value={formData.documentType}
                  onChange={(e) => setFormData({...formData, documentType: e.target.value})}
                  required
                >
                  {documentTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Document Name *</label>
                <input
                  type="text"
                  value={formData.documentName}
                  onChange={(e) => setFormData({...formData, documentName: e.target.value})}
                  required
                  placeholder="Enter document name"
                />
              </div>

              <div className="form-group">
                <label>Access Level *</label>
                <select
                  value={formData.accessLevel}
                  onChange={(e) => setFormData({...formData, accessLevel: e.target.value})}
                  required
                >
                  <option value="Public">Public</option>
                  <option value="Judge">Judge Only</option>
                  <option value="Advocate">Advocate & Judge</option>
                  <option value="Restricted">Restricted (Admin/Clerk Only)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Upload Date *</label>
                <input
                  type="date"
                  value={formData.uploadDate}
                  onChange={(e) => setFormData({...formData, uploadDate: e.target.value})}
                  required
                />
              </div>

              <div className="form-group full-width">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows="4"
                  placeholder="Enter document description"
                />
              </div>

              <div className="form-group full-width">
                <label>File Upload (Mock)</label>
                <input
                  type="file"
                  disabled
                  className="file-input"
                />
                <small className="file-note">Note: File upload is mocked for this demo. In production, this would handle actual file uploads.</small>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingId ? 'Update' : 'Upload'} Document
              </button>
              <button type="button" onClick={resetForm} className="btn-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="documents-table-card">
        <h2>Documents ({documents.length})</h2>
        {documents.length > 0 ? (
          <div className="table-container">
            <table className="documents-table">
              <thead>
                <tr>
                  <th>Case Number</th>
                  <th>Document Name</th>
                  <th>Type</th>
                  <th>Upload Date</th>
                  <th>Uploaded By</th>
                  <th>Access Level</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map(doc => {
                  const hasAccess = canAccess(doc);
                  return (
                    <tr key={doc.id} className={!hasAccess ? 'restricted' : ''}>
                      <td><strong>{doc.caseNumber}</strong></td>
                      <td>{doc.documentName}</td>
                      <td><span className="doc-type-badge">{doc.documentType}</span></td>
                      <td>{new Date(doc.uploadDate).toLocaleDateString()}</td>
                      <td>{doc.uploadedBy}</td>
                      <td>
                        <span className={`access-badge access-${doc.accessLevel.toLowerCase()}`}>
                          {doc.accessLevel}
                        </span>
                      </td>
                      <td>
                        {hasAccess ? (
                          <>
                            <button className="btn-view">View</button>
                            <button className="btn-download">Download</button>
                            {(user?.role === 'Admin' || user?.role === 'Clerk') && (
                              <>
                                <button onClick={() => handleEdit(doc)} className="btn-edit">Edit</button>
                                <button onClick={() => handleDelete(doc.id)} className="btn-delete">Delete</button>
                              </>
                            )}
                          </>
                        ) : (
                          <span className="no-access">No Access</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="empty-state">No documents uploaded yet. Click "Upload Document" to get started.</p>
        )}
      </div>
    </div>
  );
};

export default DocumentManagement;

